export const UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID = -2;
export const UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL = "未指定診間";

/** 表單診間下拉「未指定診間」（與同名真實診間用紅底 hover 區隔） */
export const CLEAR_CONSULTING_ROOM_SELECT_LABEL = "未指定診間";

export function buildClearConsultingRoomSelectOption() {
  return {
    value: "",
    label: CLEAR_CONSULTING_ROOM_SELECT_LABEL,
    className: "appointment-select-option--sentinel",
  };
}

/** 診間 id 以 consulting_room_id 為準（含明確 null）；僅在無該欄位時才 fallback camelCase。 */
export function resolveEffectiveConsultingRoomId(appointment) {
  if (!appointment || typeof appointment !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(appointment, "consulting_room_id")) {
    return appointment.consulting_room_id;
  }
  if (Object.prototype.hasOwnProperty.call(appointment, "consultingRoomId")) {
    return appointment.consultingRoomId;
  }
  return null;
}

export function isAppointmentWithoutConsultingRoom(appointment) {
  const roomId = resolveEffectiveConsultingRoomId(appointment);
  if (roomId == null || roomId === "") return true;
  const num = Number(roomId);
  if (Number.isNaN(num)) return true;
  if (num === UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID || num === 0) {
    return true;
  }
  return false;
}

export function hasUnassignedConsultingRoomAppointments(appointments = []) {
  return (appointments || []).some((appointment) =>
    isAppointmentWithoutConsultingRoom(appointment)
  );
}

export function resolveConsultingRoomFilterId(roomId) {
  const num = Number(roomId);
  if (
    roomId == null ||
    roomId === "" ||
    Number.isNaN(num) ||
    num === 0 ||
    num === UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID
  ) {
    return UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID;
  }
  return num;
}

export function resolveAppointmentConsultingRoomGroupId(appointment) {
  if (isAppointmentWithoutConsultingRoom(appointment)) {
    return UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID;
  }
  return Number(resolveEffectiveConsultingRoomId(appointment));
}

/** 表單 raw 值 → 送 API 的診間欄位。
 * create：未指定時四欄皆 null/""（與新增預約一致）
 * update：未指定時送 id 0（後端 update 會忽略 null，需用 0 才能清掉既有診間 FK）
 */
export function buildConsultingRoomApiPayload(
  rawConsultingRoomId,
  roomOptions = [],
  { mode = "create" } = {}
) {
  const isUnassigned = isAppointmentWithoutConsultingRoom({
    consulting_room_id: rawConsultingRoomId,
  });

  if (isUnassigned) {
    if (mode === "update") {
      return {
        consultingRoomId: 0,
        consultingRoomName: "",
        consulting_room_id: 0,
        consulting_room_name: "",
      };
    }
    return {
      consultingRoomId: null,
      consultingRoomName: "",
      consulting_room_id: null,
      consulting_room_name: "",
    };
  }

  const consultingRoomId = rawConsultingRoomId;

  const consultingRoomName =
    roomOptions.find(
      (room) => Number(room.value) === Number(consultingRoomId)
    )?.label || "";

  return {
    consultingRoomId,
    consultingRoomName,
    consulting_room_id: consultingRoomId,
    consulting_room_name: consultingRoomName,
  };
}

/** 載入／更新後同步 snake + camel 診間欄位，避免半套資料 */
export function normalizeAppointmentConsultingRoomFields(
  appointment,
  rooms = []
) {
  if (!appointment || typeof appointment !== "object") return appointment;

  if (isAppointmentWithoutConsultingRoom(appointment)) {
    return {
      ...appointment,
      consulting_room_id: null,
      consultingRoomId: null,
      consulting_room_name: "",
      consultingRoomName: "",
    };
  }

  const id = resolveEffectiveConsultingRoomId(appointment);
  const resolvedName =
    appointment.consulting_room_name ||
    appointment.consultingRoomName ||
    rooms.find((room) => Number(room.id) === Number(id))?.name ||
    "";

  return {
    ...appointment,
    consulting_room_id: id,
    consultingRoomId: id,
    consulting_room_name: resolvedName,
    consultingRoomName: resolvedName,
  };
}

export function resolveConsultingRoomDisplayLabel(appointment, rooms = []) {
  if (isAppointmentWithoutConsultingRoom(appointment)) {
    return UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL;
  }
  const name =
    appointment?.consulting_room_name || appointment?.consultingRoomName;
  if (name) return name;
  const id = resolveEffectiveConsultingRoomId(appointment);
  // 有診間 id 但名稱尚未從診間清單解析時，勿顯示「未指定診間」（載入中會閃一下）
  return (
    rooms.find((room) => Number(room.id) === Number(id))?.name || ""
  );
}

export function buildUnassignedConsultingRoomResource(generateAvatarSvg) {
  return {
    id: UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID,
    value: UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID,
    label: UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL,
    text: UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL,
    name: UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL,
    is_archived: false,
    speciality: "診間",
    image: generateAvatarSvg
      ? generateAvatarSvg(UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL)
      : "",
    backgroundColor: "#9AA5B1",
  };
}

export function buildConsultingRoomFilterOptions(rooms = []) {
  const realRooms = (rooms || [])
    .filter((room) => room?.id != null && room?.id !== "" && Number(room.id) !== 0)
    .slice()
    .sort((a, b) =>
      String(a.name ?? "").localeCompare(String(b.name ?? ""), "zh-Hant")
    );

  return [
    {
      id: UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID,
      name: UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL,
    },
    ...realRooms,
  ];
}

export function getAllSelectableConsultingRoomIds(rooms = []) {
  return buildConsultingRoomFilterOptions(rooms).map((room) => Number(room.id));
}
